// SDP modifiers applied to every outgoing offer/answer (sip.js
// sessionDescriptionHandlerModifiers).
//
// Why pruning is load-bearing and not just an optimization: FreeSWITCH's
// WebSocket frame parser wedges the connection when several SIP frames arrive
// coalesced in one socket read and a large frame straddles its read-buffer
// boundary. Reproduced deterministically against dev (2026-07-28): send
// tiny + tiny + 7KB OPTIONS back-to-back on one WSS connection and the 7KB
// message plus everything after it is silently swallowed - the socket stays
// open but FreeSWITCH never processes another frame from it. sip.js sends
// ACK + authenticated INVITE back-to-back, and modern Chrome's video SDP
// (multi-profile H264, H265, AV1, VP9...) pushes that INVITE to ~7KB, hitting
// the bug on every video call. Pruning the video m-line to the codecs
// FreeSWITCH actually supports (VP8, H264 - see internal.xml codec-prefs)
// keeps the INVITE at ~3KB, well under the wedge threshold, and drops nothing
// FreeSWITCH could have negotiated anyway.
const KEEP_VIDEO_CODECS = ['VP8', 'H264'];

export function pruneVideoCodecs(description) {
    if(!description?.sdp) return Promise.resolve(description);

    const sections = description.sdp.split(/(?=m=)/);
    const sdp = sections
        .map((section) => section.startsWith('m=video') ? pruneVideoSection(section) : section)
        .join('');

    return Promise.resolve({ ...description, sdp });
}

function pruneVideoSection(section) {
    const lines = section.split('\r\n');

    const codecByPayload = {};
    const rtxAptByPayload = {};
    for(const line of lines) {
        const rtpmap = line.match(/^a=rtpmap:(\d+) ([^/]+)/);
        if(rtpmap) codecByPayload[rtpmap[1]] = rtpmap[2];
        const rtxApt = line.match(/^a=fmtp:(\d+) apt=(\d+)/);
        if(rtxApt) rtxAptByPayload[rtxApt[1]] = rtxApt[2];
    }

    // Chrome offers H264 as several payloads (baseline/main/high profiles etc.),
    // all literally named "H264" - matching by codec name alone (as this used to)
    // keeps every one of them, landing back near the ~6-7KB wedge threshold this
    // pruning exists to avoid. Keep only the lowest-numbered H264 payload; one
    // profile is all FreeSWITCH negotiates anyway (see internal.xml codec-prefs).
    const keep = new Set();
    let h264Kept = false;
    for(const [payload, codec] of Object.entries(codecByPayload)) {
        if(codec === 'H264') {
            if(h264Kept) continue;
            h264Kept = true;
        } else if(!KEEP_VIDEO_CODECS.includes(codec)) {
            continue;
        }
        keep.add(payload);
    }
    for(const [payload, apt] of Object.entries(rtxAptByPayload)) {
        if(codecByPayload[payload] === 'rtx' && keep.has(apt)) keep.add(payload);
    }

    return lines
        .filter((line) => {
            const perPayload = line.match(/^a=(?:rtpmap|fmtp|rtcp-fb):(\d+)/);
            return perPayload ? keep.has(perPayload[1]) : true;
        })
        .map((line) => {
            const mline = line.match(/^(m=video \S+ \S+ )(.+)$/);
            if(!mline) return line;
            const payloads = mline[2].split(' ').filter((payload) => keep.has(payload));
            // A rejected m-line (port 0, answering "no video" mid-call) has no
            // a=rtpmap lines to match against, so keep ends up empty here -
            // emitting a payload-less m-line ("m=video 0 ... " with nothing
            // after) is invalid SDP and setRemoteDescription/setLocalDescription
            // throws on it, killing the whole call. Since these modifiers run
            // on incoming answers too (sip.js applies them to both local and
            // remote descriptions), leave the section untouched rather than
            // produce something unparseable.
            if(payloads.length === 0) return line;
            return mline[1] + payloads.join(' ');
        })
        .join('\r\n');
}

// FreeSWITCH's own answer to a video m-line added via a late re-INVITE (the
// mid-call video-upgrade path) uses a completely different ice-ufrag/ice-pwd
// and port than the pre-existing audio m-line, even though it still declares
// them bundled (a=group:BUNDLE 0 1) - spec-inconsistent, and confirmed live
// via chrome://webrtc-internals to make Chrome stand up a second, genuinely
// separate transport for video that then fails ICE entirely (dtlsState
// stuck at "new", iceState "failed") while audio's own transport stays
// unaffected. Video media never arrives as a result, even though signaling
// looks fully successful.
//
// Fix: make both sides agree up front that video is an independent,
// non-bundled m-line, matching what FreeSWITCH's answer already effectively
// does anyway. Runs on both our own locally-generated offer/answer AND on
// whatever we receive back (sip.js reuses the same modifiers array for both
// directions - see Session.invite()'s sessionDescriptionHandlerModifiersReInvite
// handling), so the ice-ufrag/pwd rewrite below only fires when video's
// credentials are still identical to audio's (i.e. genuinely still
// bundled/shared - true only for our own freshly-generated local SDP, since
// Chrome bundles by default). FreeSWITCH's answer already has distinct
// per-m-line credentials, so this check naturally leaves those untouched -
// only the now-mismatched BUNDLE grouping itself needs stripping there.
const ICE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
function randomIceString(length) {
    let result = '';
    for(let i = 0; i < length; i++) {
        result += ICE_CHARS[Math.floor(Math.random() * ICE_CHARS.length)];
    }
    return result;
}

export function debundleVideo(description) {
    if(!description?.sdp) return Promise.resolve(description);

    const sections = description.sdp.split(/(?=m=)/);
    const audioSection = sections.find((section) => section.startsWith('m=audio'));
    const videoIndex = sections.findIndex((section) => section.startsWith('m=video'));
    if(!audioSection || videoIndex === -1) return Promise.resolve(description);

    const videoMid = (sections[videoIndex].match(/^a=mid:(\S+)/m) || [])[1];
    const audioUfrag = (audioSection.match(/^a=ice-ufrag:(.*)$/m) || [])[1];
    const videoUfrag = (sections[videoIndex].match(/^a=ice-ufrag:(.*)$/m) || [])[1];

    // Still sharing audio's ICE credentials - this is our own local SDP,
    // still bundled as Chrome generated it. Give video its own so the
    // browser stands up an independent transport for it, instead of one
    // that's nominally bundled but silently split from audio's on the
    // far end's answer.
    if(audioUfrag && videoUfrag && audioUfrag === videoUfrag) {
        sections[videoIndex] = sections[videoIndex]
            .replace(/^a=ice-ufrag:.*$/m, `a=ice-ufrag:${randomIceString(8)}`)
            .replace(/^a=ice-pwd:.*$/m, `a=ice-pwd:${randomIceString(24)}`);
    }

    sections[0] = sections[0].replace(/^(a=group:BUNDLE.*)$/m, (line) => {
        if(!videoMid) return line;
        return line.split(' ').filter((token) => token !== videoMid).join(' ');
    });

    const sdp = sections.join('');
    return Promise.resolve({ ...description, sdp });
}
